import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchQuickBgvFinalReport, getS3key } from "../../services/employee/api";
import logo from "../../assets/images/Trueface.png";
import html2pdf from "html2pdf.js";
import {
  Download,
  ArrowLeft,
  ExternalLink,
  FileText,
  ImageIcon,
  User,
  FileCheck,
  Briefcase,
  ShieldAlert
} from "lucide-react";

/* Dynamic PDF.js Loader for Rendering PDF Proofs on Canvas */
const loadPdfJs = () => {
  return new Promise((resolve, reject) => {
    if (window.pdfjsLib) {
      resolve(window.pdfjsLib);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
      resolve(window.pdfjsLib);
    };
    script.onerror = (err) => reject(err);
    document.head.appendChild(script);
  });
};

/* Document Preview Component */
const ReportDocPreview = ({ fileUrl, fileName }) => {
  const isPdf =
    fileName?.toLowerCase().endsWith(".pdf") ||
    fileUrl?.toLowerCase().includes(".pdf");

  const canvasRef = useRef(null);
  const [pdfRendered, setPdfRendered] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(isPdf);

  useEffect(() => {
    let isMounted = true;

    if (isPdf && fileUrl) {
      setLoadingPdf(true);

      loadPdfJs()
        .then((pdfjs) => pdfjs.getDocument(fileUrl).promise)
        .then((pdf) => pdf.getPage(1))
        .then((page) => {
          if (!isMounted || !canvasRef.current) return;
          const canvas = canvasRef.current;
          const context = canvas.getContext("2d");
          const viewport = page.getViewport({ scale: 1.5 });

          canvas.height = viewport.height;
          canvas.width = viewport.width;

          const renderContext = {
            canvasContext: context,
            viewport: viewport
          };

          return page.render(renderContext).promise;
        })
        .then(() => {
          if (isMounted) {
            setPdfRendered(true);
            setLoadingPdf(false);
          }
        })
        .catch((err) => {
          console.error("PDF Canvas rendering error:", err);
          if (isMounted) {
            setPdfRendered(false);
            setLoadingPdf(false);
          }
        });
    }

    return () => {
      isMounted = false;
    };
  }, [fileUrl, isPdf]);

  return (
    <div
      className="pdf-avoid-break doc-preview-container"
      style={{
        border: "1px solid #cbd5e1",
        borderRadius: "8px",
        padding: "12px",
        background: "#ffffff",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        width: "100%",
        boxSizing: "border-box",
        pageBreakInside: "avoid",
        breakInside: "avoid",
        marginBottom: "12px",
        overflow: "hidden"
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #f1f5f9",
          paddingBottom: "6px"
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {isPdf ? (
            <FileText size={16} color="#dc2626" />
          ) : (
            <ImageIcon size={16} color="#2563eb" />
          )}
          <span
            style={{
              fontSize: "11px",
              fontWeight: "700",
              color: "#1e293b",
              wordBreak: "break-all"
            }}
          >
            {fileName || "Verification_Attached_Document"}
          </span>
        </div>
        <a
          href={fileUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            fontSize: "11px",
            color: "#2563eb",
            fontWeight: "700",
            textDecoration: "none"
          }}
        >
          <ExternalLink size={12} /> Open Original
        </a>
      </div>

      <div
        style={{
          width: "100%",
          background: "#f8fafc",
          borderRadius: "4px",
          overflow: "hidden",
          textAlign: "center"
        }}
      >
        {isPdf ? (
          <div style={{ width: "100%", padding: "4px", height: "auto" }}>
            {loadingPdf && (
              <div style={{ padding: "20px", fontSize: "11px", color: "#475569" }}>
                Rendering PDF Document...
              </div>
            )}
            <canvas
              ref={canvasRef}
              className="pdf-preview-canvas"
              style={{
                width: "100%",
                height: "auto",
                display: pdfRendered ? "block" : "none",
                borderRadius: "4px"
              }}
            />
          </div>
        ) : (
          <img
            src={fileUrl}
            alt={fileName || "Attachment Preview"}
            style={{
              width: "100%",
              height: "auto",
              display: "block",
              objectFit: "contain",
              margin: "0 auto"
            }}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        )}
      </div>
    </div>
  );
};

/* Status Badge Helper */
const StatusBadge = ({ status }) => {
  if (status === null || status === undefined || status === "") {
    status = "Pending";
  }

  if (status === true || status === "true" || status === "TRUE" || status === 1) {
    status = "Verified";
  } else if (status === false || status === "false" || status === "FALSE" || status === 0) {
    status = "Flagged";
  }

  const normalized = String(status || "").trim().toLowerCase();

  // Default / Review / Pending (Gold / Yellow / Amber theme)
  let bg = "#fefce8";
  let color = "#b45309";
  let border = "#fde047";
  let text = String(status).toUpperCase();

  if (
    normalized.includes("clear") ||
    normalized.includes("verified") ||
    normalized.includes("pass") ||
    normalized.includes("approved") ||
    normalized.includes("completed")
  ) {
    bg = "#e8f5e9";
    color = "#2e7d32";
    border = "#c8e6c9";
    text = "✓ " + (normalized.includes("pass") ? "Passed" : "Verified");
  } else if (
    normalized.includes("review") ||
    normalized.includes("disposed") ||
    normalized.includes("closed") ||
    normalized.includes("warning")
  ) {
    bg = "#fefce8";
    color = "#ca8a04";
    border = "#fde047";
    text = String(status).toUpperCase();
  } else if (
    normalized.includes("pending") ||
    normalized.includes("not started") ||
    normalized.includes("in progress")
  ) {
    bg = "#fffbeb";
    color = "#d97706";
    border = "#fde68a";
    text = "⏳ Pending";
  } else if (
    normalized.includes("flag") ||
    normalized.includes("fail") ||
    normalized.includes("reject") ||
    normalized.includes("risk")
  ) {
    bg = "#fef2f2";
    color = "#dc2626";
    border = "#fca5a5";
    text = "⚠ Flagged";
  }

  return (
    <span
      style={{
        background: bg,
        color: color,
        border: `1px solid ${border}`,
        padding: "3px 8px",
        borderRadius: "4px",
        fontWeight: "600",
        fontSize: "10px",
        display: "inline-block",
        lineHeight: "1.2",
        whiteSpace: "nowrap"
      }}
    >
      {text}
    </span>
  );
};


const QuickBgvFinalReport = ({ candidateId: propCandidateId, onBack }) => {
  const { candidateId: routeCandidateId } = useParams();
  const candidateId = propCandidateId || routeCandidateId;
  const navigate = useNavigate();

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [proofDocuments, setProofDocuments] = useState([]);

  useEffect(() => {
    if (candidateId) {
      fetchReportDetails();
    }
  }, [candidateId]);

  const fetchReportDetails = async () => {
    setLoading(true);
    try {
      const response = await fetchQuickBgvFinalReport(candidateId);
      const data = response?.data?.data || response?.data || null;
      setReportData(data);
    } catch (err) {
      console.error("Error fetching Quick BGV report data:", err);
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  // Proof resolution logic
  useEffect(() => {
    if (!reportData) return;

    const loadProof = async (proofPath, title) => {
      if (!proofPath || typeof proofPath !== "string") return null;
      try {
        let cleanKey = proofPath;
        if (proofPath.startsWith("http://") || proofPath.startsWith("https://")) {
          const urlParts = proofPath.split(".amazonaws.com/");
          if (urlParts.length > 1) cleanKey = urlParts[1];
        }
        cleanKey = cleanKey.split("?")[0];
        const fileName = title || cleanKey.split("/").pop() || "Document_Proof";

        if (proofPath.startsWith("http") || proofPath.startsWith("data:")) {
          return { fileName, url: proofPath };
        }

        const response = await getS3key(cleanKey);
        if (!response || !response.data) return null;

        const fileBlob =
          response.data instanceof Blob
            ? response.data
            : new Blob([response.data]);

        if (fileBlob.size === 0) return null;
        return { fileName, url: URL.createObjectURL(fileBlob) };
      } catch (error) {
        console.error("Error loading proof asset:", error);
        return null;
      }
    };

    const fetchAllProofs = async () => {
      const docs = [];
      const proofs = reportData?.proofs || {};

      if (proofs.identityUrl) {
        const doc = await loadProof(proofs.identityUrl, "Identity Document Proof");
        if (doc) docs.push(doc);
      }
      if (proofs.currentAddressUrl) {
        const doc = await loadProof(proofs.currentAddressUrl, "Address Verification Proof");
        if (doc) docs.push(doc);
      }
      if (proofs.permanentAddressUrl && proofs.permanentAddressUrl !== proofs.currentAddressUrl) {
        const doc = await loadProof(proofs.permanentAddressUrl, "Permanent Address Proof");
        if (doc) docs.push(doc);
      }
      if (proofs.experienceDocUrl) {
        const doc = await loadProof(proofs.experienceDocUrl, "Employment / Experience Certificate");
        if (doc) docs.push(doc);
      }

      setProofDocuments(docs);
    };

    fetchAllProofs();
  }, [reportData]);

  // Dynamic Address Extraction
  const resolveDynamicAddress = (data) => {
    if (!data || typeof data !== "object") return "N/A";

    if (data.fullAddress && String(data.fullAddress).trim()) return String(data.fullAddress).trim();
    if (data.address && String(data.address).trim()) return String(data.address).trim();

    const addressSegments = [
      data.house,
      data.street,
      data.landmark,
      data.location,
      data.vtc,
      data.postOffice ? `PO: ${data.postOffice}` : null,
      data.subDistrict,
      data.district,
      data.state,
      data.zip || data.pincode,
      data.country
    ].filter((segment) => {
      if (!segment) return false;
      const str = String(segment).trim();
      return str !== "" && str.toLowerCase() !== "null" && str.toLowerCase() !== "undefined";
    });

    return addressSegments.length > 0 ? addressSegments.join(", ") : "N/A";
  };

  // PDF Export Engine
  const handleDownload = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();

    const reportElement = document.getElementById("quick-final-report-container");
    if (!reportElement) return;

    const printStyles = document.createElement("style");
    printStyles.innerHTML = `
      #quick-final-report-container {
        width: 794px !important;
        margin: 0 auto !important;
        background: #ffffff !important;
      }
      .pdf-avoid-break {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
    `;
    document.head.appendChild(printStyles);
    reportElement.classList.add("pdf-export-mode");

    const loadImageAsBase64 = async (url) => {
      try {
        if (!url) return null;
        if (url.startsWith("data:")) return url;

        const response = await fetch(url, { mode: "cors" });
        if (!response.ok) return null;

        const blob = await response.blob();
        return await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        return null;
      }
    };

    try {
      const images = Array.from(reportElement.querySelectorAll("img"));
      await Promise.all(
        images.map(async (img) => {
          const base64 = await loadImageAsBase64(img.src);
          if (base64) img.src = base64;
        })
      );

      await new Promise((r) => setTimeout(r, 300));

      const opt = {
        margin: [10, 0, 10, 0],
        filename: `Quick_BGV_Report_${candidateId}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: false,
          backgroundColor: "#ffffff",
          scrollY: 0,
          scrollX: 0
        },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["css", "legacy"] }
      };

      await html2pdf().set(opt).from(reportElement).save();
    } catch (err) {
      console.error("PDF Download Failure:", err);
    } finally {
      reportElement.classList.remove("pdf-export-mode");
      if (printStyles.parentNode) {
        document.head.removeChild(printStyles);
      }
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", background: "#f8fafc" }}>
        <h2 style={{ fontFamily: "Arial", color: "#001144", fontWeight: "700" }}>Loading Quick BGV Report...</h2>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100vh", background: "#f8fafc", gap: "10px" }}>
        <h2 style={{ fontFamily: "Arial", color: "#001144", fontWeight: "700" }}>No Report Data Found</h2>
        <button
          onClick={() => (onBack ? onBack() : navigate(-1))}
          style={{
            padding: "8px 16px",
            background: "#001144",
            color: "#ffffff",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "600"
          }}
        >
          Go Back
        </button>
      </div>
    );
  }

  // Exact Data Properties from Backend QuickBgvFullVerificationResponse
  const aadhaarData = reportData?.aadhaar?.data;
  const isAadhaarDone = Boolean(
    (aadhaarData && Object.keys(aadhaarData).length > 0) ||
    String(reportData?.aadhaar?.status || "").toUpperCase() === "COMPLETED" ||
    String(reportData?.aadhaar?.status || "").toUpperCase() === "VERIFIED"
  );

  const rawUanData = reportData?.uan?.data;
  const uanList = Array.isArray(rawUanData)
    ? rawUanData
    : rawUanData
      ? [rawUanData]
      : [];
  const isUanCompleted = Boolean(
    String(reportData?.uan?.status || "").toUpperCase() === "COMPLETED" ||
    String(reportData?.uan?.status || "").toUpperCase() === "VERIFIED" ||
    (reportData?.uan?.data && (uanList.length > 0 || Object.keys(reportData.uan.data).length > 0))
  );
  const hasUanRecords = uanList.length > 0 && uanList.some(item => item.companyName || item.establishmentName || item.memberId);

  // Criminal Data Normalization & Flattening Cases
  const rawCriminalData = reportData?.criminal?.data;
  const criminalChecks = Array.isArray(rawCriminalData)
    ? rawCriminalData
    : rawCriminalData
      ? [rawCriminalData]
      : [];

  const criminalItem = criminalChecks[0] || {};
  const criminalStatusRaw = String(
    reportData?.criminal?.status ||
    criminalItem.criminalCheckStatus ||
    criminalItem.checkStatus ||
    ""
  ).trim().toUpperCase();

  const isCriminalDone = Boolean(
    criminalStatusRaw &&
    criminalStatusRaw !== "PENDING" &&
    criminalStatusRaw !== "IN PROGRESS" &&
    criminalStatusRaw !== "NOT STARTED" &&
    (criminalChecks.length > 0 || String(reportData?.criminal?.status || "").toUpperCase() === "COMPLETED")
  );

  const isCriminalFailed = criminalStatusRaw === "FAIL" || criminalStatusRaw === "FLAGGED" || criminalStatusRaw === "HIGH";

  // Extract all cases from nested arrays
  const extractedCases = [];
  if (isCriminalDone) {
    criminalChecks.forEach((chk) => {
      const list = chk.cases || chk.caseDetails || chk.result || chk.data || [];
      if (Array.isArray(list) && list.length > 0) {
        list.forEach((cs) => {
          extractedCases.push({
            caseNumber: cs.caseNumber || cs.case_number || cs.caseNo || "N/A",
            courtName: cs.courtName || cs.court_name || cs.court || "N/A",
            petitioner: cs.petitioner || cs.petitionerName || "N/A",
            respondent: cs.respondent || cs.respondentName || "N/A",
            caseStatus: cs.caseStatus || cs.case_status || cs.status || "Flagged",
            year: cs.year || "N/A"
          });
        });
      } else if (chk.caseNumber || chk.case_number || chk.courtName || chk.court_name) {
        extractedCases.push({
          caseNumber: chk.caseNumber || chk.case_number || chk.caseNo || "N/A",
          courtName: chk.courtName || chk.court_name || chk.court || "N/A",
          petitioner: chk.petitioner || chk.petitionerName || "N/A",
          respondent: chk.respondent || chk.respondentName || "N/A",
          caseStatus: chk.caseStatus || chk.case_status || chk.status || "Flagged",
          year: chk.year || "N/A"
        });
      }
    });
  }

  const resolvedAddress = resolveDynamicAddress(aadhaarData);

  // Dynamic Candidate Fields Resolution across all modules
  const candidateDisplayName =
    aadhaarData?.fullName ||
    aadhaarData?.name ||
    reportData?.candidateInfo?.fullName ||
    reportData?.candidateInfo?.name ||
    reportData?.candidateName ||
    reportData?.name ||
    uanList?.[0]?.employeeName ||
    uanList?.[0]?.name ||
    uanList?.[0]?.memberName ||
    reportData?.uan?.name ||
    criminalItem?.candidateName ||
    criminalItem?.name ||
    reportData?.criminal?.name ||
    "N/A";

  const candidateDob =
    aadhaarData?.dob ||
    aadhaarData?.metadataDob ||
    reportData?.candidateInfo?.dob ||
    reportData?.candidateInfo?.dateOfBirth ||
    reportData?.dateOfBirth ||
    reportData?.dob ||
    uanList?.[0]?.dob ||
    criminalItem?.dob ||
    "N/A";

  const candidateFatherName =
    aadhaarData?.fatherName ||
    aadhaarData?.careOf ||
    reportData?.candidateInfo?.fatherName ||
    reportData?.fatherName ||
    uanList?.[0]?.fatherName ||
    uanList?.[0]?.husbandName ||
    criminalItem?.fatherName ||
    "N/A";

  const candidateGender =
    aadhaarData?.gender ||
    aadhaarData?.metadataGender ||
    reportData?.candidateInfo?.gender ||
    reportData?.gender ||
    uanList?.[0]?.gender ||
    "N/A";

  // Criminal Check Parameters Extraction
  const candidateState =
    aadhaarData?.state ||
    criminalItem?.state ||
    reportData?.criminal?.state ||
    reportData?.state ||
    "N/A";

  const candidateSearchYear =
    criminalItem?.year ||
    criminalItem?.searchYear ||
    reportData?.criminal?.year ||
    "N/A";

  const candidateLocationOrAddress =
    resolvedAddress !== "N/A"
      ? resolvedAddress
      : criminalItem?.address ||
      criminalItem?.district ||
      reportData?.criminal?.address ||
      "N/A";

  // Flag to check if standard personal identity (DOB/Gender) is missing
  const hasDobOrGender = candidateDob !== "N/A" || candidateGender !== "N/A";

  // Dynamic Candidate Type Calculation
  const resolveCandidateType = () => {
    const rawType =
      reportData?.candidateType ||
      reportData?.candidateInfo?.candidateType ||
      reportData?.candidateInfo?.type ||
      reportData?.type ||
      reportData?.employmentType ||
      "";

    if (rawType) {
      const lower = String(rawType).toLowerCase();
      if (lower.includes("exp")) return "Experienced Professional";
      if (lower.includes("fresh")) return "Fresher";
    }

    if (isUanCompleted && hasUanRecords) {
      return "Experienced Professional";
    }

    return "Fresher";
  };

  const dynamicCandidateType = resolveCandidateType();

  // ACCURATE LOGIC FOR QA RANGES
const criminalRecordCount = extractedCases.length;

// 1. High Risk = Count of ONLY active/unresolved cases (e.g., PENDING, UNDER TRIAL)
const highRiskCount = isCriminalDone && isCriminalFailed
  ? Math.max(1, criminalRecordCount)
  : extractedCases.filter(
      (c) => c?.caseStatus && !["disposed", "closed", "clear", "pass"].includes(String(c.caseStatus).toLowerCase())
    ).length;

// 2. Medium Risk = Count of historical/resolved cases (DISPOSED or CLOSED)
const mediumRiskCount = extractedCases.filter(
  (c) => c?.caseStatus && ["disposed", "closed"].includes(String(c.caseStatus).toLowerCase())
).length;

let lowRiskCount = 0;
if (isAadhaarDone) lowRiskCount += 1; // Only Aadhaar done -> 1
if (isUanCompleted) lowRiskCount += 1; // UAN done -> +1
if (isCriminalDone && !isCriminalFailed && criminalRecordCount === 0) {
  lowRiskCount += 1; // Criminal done with no adverse records -> +1
}

// 4. Overall Status Determination
let dynamicOverallStatus = "CLEAR";
let dynamicOverallRisk = "Low";

if (highRiskCount > 0) {
  dynamicOverallStatus = "FLAGGED";
  dynamicOverallRisk = "High";
} else if (mediumRiskCount > 0) {
  dynamicOverallStatus = "REVIEW";
  dynamicOverallRisk = "Medium";
} else if (!isAadhaarDone && !isUanCompleted && !isCriminalDone) {
  dynamicOverallStatus = "PENDING";
  dynamicOverallRisk = "Low";
} else {
  dynamicOverallStatus = "CLEAR";
  dynamicOverallRisk = "Low";
}

  let sectionCounter = 1;

  // Shared Styles
  const headerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 36px",
    backgroundColor: "#001144",
    color: "#ffffff"
  };

  const logoContainerStyle = {
    background: "#ffffff",
    padding: "4px 12px",
    borderRadius: "6px",
    display: "inline-flex",
    alignItems: "center",
    gap: "10px"
  };

  const sectionHeaderStyle = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "0 36px",
    marginTop: "20px",
    marginBottom: "10px"
  };

  const tableHeaderStyle = {
    backgroundColor: "#001144",
    color: "#ffffff",
    padding: "8px 10px",
    textAlign: "left",
    fontSize: "11px",
    fontWeight: "600",
    fontFamily: "Arial, sans-serif"
  };

  const cellStyle = {
    padding: "7px 10px",
    borderBottom: "1px solid #cbd5e1",
    fontSize: "10px",
    color: "#1a1a1a",
    fontFamily: "Arial, sans-serif",
    whiteSpace: "normal",
    wordBreak: "normal",
    overflowWrap: "break-word",
    verticalAlign: "middle"
  };

  const labelCellStyle = {
    ...cellStyle,
    background: "#f8fafc",
    color: "#002266",
    fontWeight: "600",
    width: "130px",
    border: "1px solid #cbd5e1"
  };

  const valueCellStyle = { ...cellStyle, border: "1px solid #cbd5e1" };

  const pdfSectionNumStyle = {
    width: "20px",
    height: "20px",
    background: "#001144",
    color: "#ffffff",
    fontSize: "10px",
    fontWeight: "600",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  };

  const pdfSectionTitleStyle = {
    fontSize: "11.5px",
    fontWeight: "700",
    color: "#001144",
    textTransform: "uppercase",
    fontFamily: "Arial, sans-serif"
  };

  const liveUiFooterStyle = {
    borderTop: "1px solid #cbd5e1",
    padding: "14px 36px",
    display: "flex",
    justifyContent: "space-between",
    fontSize: "11px",
    color: "#495f7e",
    fontFamily: "Arial",
    backgroundColor: "#ffffff",
    marginTop: "30px"
  };

  return (
    <div
      style={{
        padding: "20px",
        background: "#e8e8e8",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh"
      }}
    >
      {/* Top Action Header */}
      <div
        style={{
          width: "794px",
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "15px"
        }}
      >
        <button
          onClick={() => (onBack ? onBack() : navigate(-1))}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "8px 16px",
            background: "#ffffff",
            border: "1px solid #cbd5e1",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "600",
            fontSize: "12px",
            color: "#1e293b"
          }}
        >
          <ArrowLeft size={16} /> Back
        </button>

        <button
          onClick={handleDownload}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "8px 16px",
            background: "#001144",
            color: "#ffffff",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "600",
            fontSize: "12px"
          }}
        >
          <Download size={16} /> Download PDF Report
        </button>
      </div>

      {/* Main Document Master Container */}
      <div
        id="quick-final-report-container"
        style={{
          width: "794px",
          background: "#ffffff",
          boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
          boxSizing: "border-box",
          paddingBottom: "20px"
        }}
      >
        {/* Main Branding Header */}
        <div style={headerStyle}>
          <div style={logoContainerStyle}>
            <img src={logo} alt="TrueFace" style={{ width: "32px", height: "36px", objectFit: "contain" }} />
            <div style={{ display: "flex", flexDirection: "column", fontFamily: "Arial", lineHeight: "1.1" }}>
              <span style={{ fontSize: "20px", fontWeight: "800", color: "#052680" }}>
                True<span style={{ color: "#de8406" }}>Face</span>
              </span>
              <span style={{ fontSize: "6.5px", fontWeight: "700", color: "#092246" }}>
                Powered by Client Server Technology Solutions
              </span>
            </div>
          </div>
          <div style={{ textAlign: "right", fontSize: "10.5px", color: "#eeeeee", lineHeight: "1.3", fontFamily: "Arial" }}>
            <strong>CLIENTSERVER SOLUTIONS PVT LTD</strong><br />
            Suite#805, 9th Floor, DSL Abacus IT Park, IDA<br />
            Uppal, Hyderabad, Telangana – 500039.<br />
            hrind@clientservertech.com, +91 9640009669.
          </div>
        </div>

        {/* Candidate Title & Dynamic Candidate Type Subtitle */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 36px 10px 36px" }}>
          <div style={{ textAlign: "left", fontFamily: "Arial" }}>
            <h1 style={{ fontSize: "22px", fontWeight: "780", color: "#c4953c", margin: "0 0 2px 0" }}>
              {candidateDisplayName}
            </h1>
            <p style={{ fontSize: "12px", color: "#475569", fontWeight: "600", margin: 0 }}>
              Candidate ID: {candidateId} | <strong style={{ color: "#001144" }}>{dynamicCandidateType}</strong>
            </p>
          </div>

          {/* Dynamic Color Badge Configuration */}
          {(() => {
            const isPending = dynamicOverallStatus === "PENDING";
            const isFlagged = dynamicOverallStatus === "FLAGGED" || highRiskCount > 0;
            const isReview = dynamicOverallStatus === "REVIEW" || mediumRiskCount > 0;

            const borderColor = isFlagged
              ? "#dc2626"
              : isPending || isReview
                ? "#f59e0b"
                : "#16a34a";

            const bgColor = isFlagged
              ? "#fef2f2"
              : isPending || isReview
                ? "#fffbeb"
                : "#f0fdf4";

            const labelColor = isFlagged
              ? "#dc2626"
              : isPending || isReview
                ? "#d97706"
                : "#16a34a";

            const titleColor = isFlagged
              ? "#991b1b"
              : isPending || isReview
                ? "#b45309"
                : "#15803d";

            return (
              <div
                style={{
                  border: `2px solid ${borderColor}`,
                  padding: "8px 18px",
                  borderRadius: "8px",
                  background: bgColor,
                  textAlign: "center",
                  minWidth: "120px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)"
                }}
              >
                <div
                  style={{
                    fontSize: "9px",
                    fontWeight: "700",
                    color: labelColor,
                    letterSpacing: "1.2px",
                    textTransform: "uppercase"
                  }}
                >
                  OVERALL
                </div>
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: "900",
                    color: titleColor,
                    lineHeight: "1.2",
                    margin: "2px 0"
                  }}
                >
                  {dynamicOverallStatus}
                </div>
                <div style={{ display: "flex", gap: "3px", justifyContent: "center", margin: "2px 0" }}>
                  {[...Array(5)].map((_, i) => (
                    <span key={i} style={{ color: "#f59e0b", fontSize: "12px" }}>★</span>
                  ))}
                </div>
                <div
                  style={{
                    fontSize: "10.5px",
                    fontWeight: "700",
                    color: labelColor,
                    marginTop: "2px"
                  }}
                >
                  Risk: {dynamicOverallRisk}
                </div>
              </div>
            );
          })()}
        </div>

        {/* Section 1: Candidate Basic Information */}
        <div className="pdf-avoid-break">
          <div style={sectionHeaderStyle}>
            <div style={pdfSectionNumStyle}>{sectionCounter++}</div>
            <User size={16} color="#001144" style={{ marginLeft: "2px" }} />
            <span style={pdfSectionTitleStyle}>Candidate Information</span>
            <div style={{ flex: 1, height: "1px", background: "#001144", marginLeft: "6px" }} />
          </div>
          <div style={{ padding: "0 36px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #cbd5e1" }}>
              <tbody>
                {/* CASE 1: Digilocker / Identity Verification Completed */}
                {isAadhaarDone && (
                  <>
                    <tr>
                      <td style={labelCellStyle}>Full Name</td>
                      <td style={valueCellStyle}>{candidateDisplayName}</td>
                      <td style={labelCellStyle}>Candidate ID</td>
                      <td style={valueCellStyle}>{candidateId}</td>
                    </tr>
                    <tr>
                      <td style={labelCellStyle}>Date Of Birth</td>
                      <td style={valueCellStyle}>{candidateDob}</td>
                      <td style={labelCellStyle}>Gender</td>
                      <td style={valueCellStyle}>{candidateGender}</td>
                    </tr>
                    <tr>
                      <td style={labelCellStyle}>Father's / Care of</td>
                      <td style={valueCellStyle}>{candidateFatherName}</td>
                      <td style={labelCellStyle}>Candidate Type</td>
                      <td style={valueCellStyle}>{dynamicCandidateType}</td>
                    </tr>
                    <tr>
                      <td style={labelCellStyle}>Report Status</td>
                      <td style={valueCellStyle}>
                        <StatusBadge status={dynamicOverallStatus} />
                      </td>
                      <td style={labelCellStyle}>Risk Level</td>
                      <td style={valueCellStyle}>
                        <StatusBadge
                          status={
                            dynamicOverallRisk === "Low"
                              ? "Low Risk (Clear)"
                              : dynamicOverallRisk === "Medium"
                                ? "Medium Risk (Review)"
                                : "High Risk (Flagged)"
                          }
                        />
                      </td>
                    </tr>
                  </>
                )}

                {/* CASE 2: Only Criminal & Court Verification Completed */}
                {!isAadhaarDone && isCriminalDone && (
                  <>
                    <tr>
                      <td style={labelCellStyle}>Full Name</td>
                      <td style={valueCellStyle}>{candidateDisplayName}</td>
                      <td style={labelCellStyle}>Candidate ID</td>
                      <td style={valueCellStyle}>{candidateId}</td>
                    </tr>
                    <tr>
                      <td style={labelCellStyle}>Father's / Care of</td>
                      <td style={valueCellStyle}>{candidateFatherName}</td>
                      <td style={labelCellStyle}>Candidate Type</td>
                      <td style={valueCellStyle}>{dynamicCandidateType}</td>
                    </tr>
                    <tr>
                      <td style={labelCellStyle}>State / Region</td>
                      <td style={valueCellStyle}>{candidateState}</td>
                      <td style={labelCellStyle}>Search Year</td>
                      <td style={valueCellStyle}>{candidateSearchYear}</td>
                    </tr>
                    {candidateLocationOrAddress !== "N/A" && (
                      <tr>
                        <td style={labelCellStyle}>Address / Location</td>
                        <td colSpan="3" style={valueCellStyle}>{candidateLocationOrAddress}</td>
                      </tr>
                    )}
                    <tr>
                      <td style={labelCellStyle}>Report Status</td>
                      <td style={valueCellStyle}>
                        <StatusBadge status={dynamicOverallStatus} />
                      </td>
                      <td style={labelCellStyle}>Risk Level</td>
                      <td style={valueCellStyle}>
                        <StatusBadge
                          status={
                            dynamicOverallRisk === "Low"
                              ? "Low Risk (Clear)"
                              : dynamicOverallRisk === "Medium"
                                ? "Medium Risk (Review)"
                                : "High Risk (Flagged)"
                          }
                        />
                      </td>
                    </tr>
                  </>
                )}

                {/* CASE 3: Only UAN / Employment Check Completed */}
                {!isAadhaarDone && !isCriminalDone && isUanCompleted && (
                  <>
                    <tr>
                      <td style={labelCellStyle}>Full Name</td>
                      <td style={valueCellStyle}>{candidateDisplayName}</td>
                      <td style={labelCellStyle}>Candidate ID</td>
                      <td style={valueCellStyle}>{candidateId}</td>
                    </tr>
                    <tr>
                      <td style={labelCellStyle}>Candidate Type</td>
                      <td style={valueCellStyle}>{dynamicCandidateType}</td>
                      <td style={labelCellStyle}>Service History</td>
                      <td style={valueCellStyle}>
                        {hasUanRecords ? `${uanList.length} Organization(s) Found` : "0 Active Records"}
                      </td>
                    </tr>
                    <tr>
                      <td style={labelCellStyle}>Report Status</td>
                      <td style={valueCellStyle}>
                        <StatusBadge status={dynamicOverallStatus} />
                      </td>
                      <td style={labelCellStyle}>Risk Level</td>
                      <td style={valueCellStyle}>
                        <StatusBadge
                          status={
                            dynamicOverallRisk === "Low"
                              ? "Low Risk (Clear)"
                              : dynamicOverallRisk === "Medium"
                                ? "Medium Risk (Review)"
                                : "High Risk (Flagged)"
                          }
                        />
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Section: Verification Check Summary (Without Remarks Column) */}
        <div className="pdf-avoid-break">
          <div style={sectionHeaderStyle}>
            <div style={pdfSectionNumStyle}>{sectionCounter++}</div>
            <FileCheck size={16} color="#001144" style={{ marginLeft: "2px" }} />
            <span style={pdfSectionTitleStyle}>Verification Check Summary</span>
            <div style={{ flex: 1, height: "1px", background: "#001144", marginLeft: "6px" }} />
          </div>
          <div style={{ padding: "0 36px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #cbd5e1" }}>
              <thead>
                <tr>
                  <th style={{ ...tableHeaderStyle, width: "40%" }}>Check Type</th>
                  <th style={{ ...tableHeaderStyle, width: "20%" }}>Status</th>
                  <th style={{ ...tableHeaderStyle, width: "40%" }}>Verified Value</th>
                </tr>
              </thead>
              <tbody>
                {/* 1. Identity Check Row */}
                {isAadhaarDone && (
                  <>
                    <tr>
                      <td style={cellStyle}>Personal & Identity Verification</td>
                      <td style={cellStyle}>
                        <StatusBadge status="Verified" />
                      </td>
                      <td style={cellStyle}>Name, DOB, Gender Match</td>
                    </tr>
                    <tr>
                      <td style={cellStyle}>Address Verification</td>
                      <td style={cellStyle}>
                        <StatusBadge status={resolvedAddress !== "N/A" ? "Verified" : "Flagged"} />
                      </td>
                      <td style={cellStyle}>DigiLocker Registered Address</td>
                    </tr>
                  </>
                )}

                {/* 2. Employment/UAN Check Row */}
                {isUanCompleted && (
                  <tr>
                    <td style={cellStyle}>Employment / UAN Verification</td>
                    <td style={cellStyle}>
                      <StatusBadge status={hasUanRecords ? "Verified" : "Clear"} />
                    </td>
                    <td style={cellStyle}>
                      {hasUanRecords
                        ? `EPFO Member: ${uanList[0]?.memberId || "Records Verified"}`
                        : "Verified (No History)"}
                    </td>
                  </tr>
                )}

                {/* 3. Criminal & Court Check Row */}
                {isCriminalDone && (
                  <tr>
                    <td style={cellStyle}>Criminal & Court Check</td>
                    <td style={cellStyle}>
                      <StatusBadge
                        status={
                          highRiskCount > 0
                            ? "Flagged"
                            : mediumRiskCount > 0
                              ? "Review"
                              : "Verified"
                        }
                      />
                    </td>
                    <td style={cellStyle}>
                      {criminalRecordCount > 0
                        ? `${criminalRecordCount} Record(s) Identified`
                        : "No Court Records Found"}
                    </td>
                  </tr>
                )}

                {/* Fallback if no checks completed */}
                {!isAadhaarDone && !isUanCompleted && !isCriminalDone && (
                  <tr>
                    <td colSpan="3" style={{ ...cellStyle, textAlign: "center", color: "#64748b" }}>
                      No verification checks completed for this candidate yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Section: Personal & Address Report (Rendered only if completed) */}
        {isAadhaarDone && (
          <div className="pdf-avoid-break">
            <div style={sectionHeaderStyle}>
              <div style={pdfSectionNumStyle}>{sectionCounter++}</div>
              <User size={16} color="#001144" style={{ marginLeft: "2px" }} />
              <span style={pdfSectionTitleStyle}>Personal & Address Report</span>
              <div style={{ flex: 1, height: "1px", background: "#001144", marginLeft: "6px" }} />
            </div>
            <div style={{ padding: "0 36px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #cbd5e1" }}>
                <thead>
                  <tr>
                    <th style={tableHeaderStyle}>Field</th>
                    <th style={tableHeaderStyle}>Verification Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={labelCellStyle}>Full Name</td>
                    <td style={valueCellStyle}>{candidateDisplayName}</td>
                  </tr>
                  <tr>
                    <td style={labelCellStyle}>Date Of Birth</td>
                    <td style={valueCellStyle}>{candidateDob}</td>
                  </tr>
                  <tr>
                    <td style={labelCellStyle}>Gender</td>
                    <td style={valueCellStyle}>{candidateGender}</td>
                  </tr>
                  <tr>
                    <td style={labelCellStyle}>Father's / Care of</td>
                    <td style={valueCellStyle}>{candidateFatherName}</td>
                  </tr>
                  <tr>
                    <td style={labelCellStyle}>Registered Address</td>
                    <td style={{ ...valueCellStyle, color: resolvedAddress !== "N/A" ? "#2e7d32" : "#64748b", fontWeight: "600" }}>
                      {resolvedAddress}
                    </td>
                  </tr>
                  <tr style={{ fontWeight: "bold" }}>
                    <td style={labelCellStyle}>Verification Status</td>
                    <td style={valueCellStyle}>
                      <StatusBadge status="Verified" />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Section: Employment & UAN History Report */}
        {isUanCompleted && (
          <div className="pdf-avoid-break">
            <div style={sectionHeaderStyle}>
              <div style={pdfSectionNumStyle}>{sectionCounter++}</div>
              <Briefcase size={16} color="#001144" style={{ marginLeft: "2px" }} />
              <span style={pdfSectionTitleStyle}>Employment & UAN History Report</span>
              <div style={{ flex: 1, height: "1px", background: "#001144", marginLeft: "6px" }} />
            </div>
            <div style={{ padding: "0 36px", boxSizing: "border-box" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  border: "1px solid #cbd5e1",
                  tableLayout: "fixed",
                  boxSizing: "border-box"
                }}
              >
                <thead>
                  <tr>
                    <th style={{ ...tableHeaderStyle, width: "32%", boxSizing: "border-box" }}>
                      Establishment / Company Name
                    </th>
                    <th style={{ ...tableHeaderStyle, width: "26%", boxSizing: "border-box" }}>
                      Member ID
                    </th>
                    <th style={{ ...tableHeaderStyle, width: "12%", textAlign: "center", boxSizing: "border-box" }}>
                      DOJ
                    </th>
                    <th style={{ ...tableHeaderStyle, width: "12%", textAlign: "center", boxSizing: "border-box" }}>
                      DOE
                    </th>
                    <th style={{ ...tableHeaderStyle, width: "18%", textAlign: "center", boxSizing: "border-box" }}>
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {hasUanRecords ? (
                    uanList.map((item, idx) => {
                      const company = item.companyName || item.establishmentName || "Establishment Data";
                      const memberId = item.memberId || "N/A";
                      const doj = item.dojEpf || item.doj || "N/A";
                      const doe = item.doeEpf || item.doe || "Present";
                      const status = item.status || "Verified";

                      return (
                        <tr key={idx} style={{ background: idx % 2 === 0 ? "#ffffff" : "#f8fafc" }}>
                          <td style={{ ...cellStyle, fontWeight: "600", boxSizing: "border-box" }}>
                            {company}
                          </td>
                          <td style={{ ...cellStyle, fontSize: "9.5px", fontFamily: "monospace", boxSizing: "border-box" }}>
                            {memberId}
                          </td>
                          <td style={{ ...cellStyle, textAlign: "center", whiteSpace: "nowrap", boxSizing: "border-box" }}>
                            {doj}
                          </td>
                          <td style={{ ...cellStyle, textAlign: "center", whiteSpace: "nowrap", boxSizing: "border-box" }}>
                            {doe}
                          </td>
                          <td style={{ ...cellStyle, textAlign: "center", padding: "6px 4px", boxSizing: "border-box" }}>
                            <StatusBadge status={status} />
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5" style={{ ...cellStyle, textAlign: "center", color: "#64748b", boxSizing: "border-box" }}>
                        UAN Verified - No prior employment/contribution records found in EPFO.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Section: Criminal & Court Check (Rendered only if completed) */}
        {isCriminalDone && (
          <div className="pdf-avoid-break">
            <div style={sectionHeaderStyle}>
              <div style={pdfSectionNumStyle}>{sectionCounter++}</div>
              <ShieldAlert size={16} color="#001144" style={{ marginLeft: "2px" }} />
              <span style={pdfSectionTitleStyle}>Criminal & Court Record Report</span>
              <div style={{ flex: 1, height: "1px", background: "#001144", marginLeft: "6px" }} />
            </div>
            <div style={{ padding: "0 36px" }}>
              {extractedCases.length > 0 ? (
                <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #cbd5e1" }}>
                  <thead>
                    <tr>
                      <th style={tableHeaderStyle}>Case Number</th>
                      <th style={tableHeaderStyle}>Court Name</th>
                      <th style={tableHeaderStyle}>Petitioner</th>
                      <th style={tableHeaderStyle}>Respondent</th>
                      <th style={tableHeaderStyle}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {extractedCases.map((item, idx) => (
                      <tr key={idx} style={{ background: idx % 2 === 0 ? "#ffffff" : "#f8fafc" }}>
                        <td style={cellStyle}>{item.caseNumber}</td>
                        <td style={cellStyle}>{item.courtName}</td>
                        <td style={cellStyle}>{item.petitioner}</td>
                        <td style={cellStyle}>{item.respondent}</td>
                        <td style={cellStyle}>
                          <StatusBadge status={item.caseStatus} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div
                  style={{
                    border: "1px solid #bbf7d0",
                    padding: "10px",
                    borderRadius: "6px",
                    background: "#f0fdf4",
                    color: "#16a34a",
                    fontSize: "11px",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px"
                  }}
                >
                  <span>✓</span> No Criminal records found - Candidate has a clear record across all court databases.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Section 6: Risk Analysis Matrix */}
        <div className="pdf-avoid-break">
          <div style={sectionHeaderStyle}>
            <div style={pdfSectionNumStyle}>{sectionCounter++}</div>
            <FileCheck size={16} color="#001144" style={{ marginLeft: "2px" }} />
            <span style={pdfSectionTitleStyle}>Risk Analysis Matrix</span>
            <div style={{ flex: 1, height: "1px", background: "#001144", marginLeft: "6px" }} />
          </div>
          <div style={{ padding: "0 36px", display: "flex", gap: "10px", width: "100%", boxSizing: "border-box" }}>
            <div style={{ flex: 1, border: "1px solid #fca5a5", background: "#fef2f2", padding: "8px", borderRadius: "6px", textAlign: "center" }}>
              <div style={{ fontSize: "16px", fontWeight: "800", color: "#dc2626" }}>{highRiskCount}</div>
              <div style={{ fontSize: "10.5px", fontWeight: "700", color: "#991b1b" }}>High Risk Findings</div>
            </div>
            <div style={{ flex: 1, border: "1px solid #fde047", background: "#fefce8", padding: "8px", borderRadius: "6px", textAlign: "center" }}>
              <div style={{ fontSize: "16px", fontWeight: "800", color: "#ca8a04" }}>{mediumRiskCount}</div>
              <div style={{ fontSize: "10.5px", fontWeight: "700", color: "#854d0e" }}>Medium Risk Findings</div>
            </div>
            <div style={{ flex: 1, border: "1px solid #bafd93", background: "#effff0", padding: "8px", borderRadius: "6px", textAlign: "center" }}>
              <div style={{ fontSize: "16px", fontWeight: "800", color: "#20b420" }}>{lowRiskCount}</div>
              <div style={{ fontSize: "10.5px", fontWeight: "700", color: "#1eaf5f" }}>Low Risk Findings</div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={liveUiFooterStyle}>
          <div>TrueFace</div>
          <span>Powered by Clientserver Solutions PVT LTD</span>
          <span>hrind@clientservertech.com</span>
        </div>
      </div>
    </div>
  );
};

export default QuickBgvFinalReport;